'use es6';

import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import debounce from 'react-utils/debounce';
import styled, { css } from 'styled-components';
import { OasPropType, OperationPropType } from '../../../lib/api-explorer/models/Oas';
import removeUndefinedObjects from '../../../lib/api-explorer/remove-undefined-objects';
import { getDocAnchorId } from '../utils/DocsUtils';
import { getRequestBodyExample } from '../utils/ExampleUtils';
import { updateFormData } from '../actions';
import { getAuth as getAuthSelector } from '../../TryIt/selectors';
import { getAvailableClientLibraries, getFormData, getHar, getIsMultipart, getOas, getOperation } from '../selectors/DocSelectors';
import { TABLE_BORDER_COLOR, TABLE_HEADER_BACKGROUND_COLOR } from 'HubStyleTokens/theme';
import H4 from 'UIComponents/elements/headings/H4';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import CodeSample from '../../CodeSample/components/CodeSample';
import ErrorBoundary from '../../ErrorBoundary/ErrorBoundary';
import Response from '../../Response/components/Response';
import Markdown from './Markdown';
import Params from './Params';
import PathUrl from './PathUrl';
import Requirements from './Requirements';
import Excerpt from '../../../components/endpoint/Excerpt';
import { mutateTrackerInstance } from '../../../tracking/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const sharedColumnStyles = css(["border-color:", ";border-style:solid;"], TABLE_BORDER_COLOR);
const StyledGrid = styled(UIGrid).withConfig({
  displayName: "Doc__StyledGrid",
  componentId: "sc-1uzuteg-0"
})(["&&{margin:0 0 32px 0;}"]);
const StyledPathContainer = styled(UIGridItem).withConfig({
  displayName: "Doc__StyledPathContainer",
  componentId: "sc-1uzuteg-1"
})(["&&{padding:0;}"]);
const StyledLeftColumn = styled(UIGridItem).withConfig({
  displayName: "Doc__StyledLeftColumn",
  componentId: "sc-1uzuteg-2"
})(["", " &&{padding:28px 16px 0 0;}border-width:1px 0 0 0;"], sharedColumnStyles);
const StyledRightColumn = styled(UIGridItem).withConfig({
  displayName: "Doc__StyledRightColumn",
  componentId: "sc-1uzuteg-3"
})(["", " &&{padding:28px 16px 0 16px;}background-color:", ";border-width:0 1px 1px 1px;"], sharedColumnStyles, TABLE_HEADER_BACKGROUND_COLOR);
class Doc extends Component {
  constructor(props) {
    super(props);
    this.resetFormState = () => {
      this.setState({
        hasError: false
      });
    };
    this.registerError = () => {
      const {
        initialFormData
      } = this.state;
      /*
        This is placed here in order to prevent a race condition from occurring.
        When placed in `resetFormState`, the form will only reset after hitting
        the button to acknowledge the error occurred in the boundary component
        twice. Moving it here removed this condition.
      */
      this.onFormDataChange(initialFormData);
      this.setState({
        hasError: true
      });
    };
    this.onFormDataChange = formData => {
      this.setState(prevState => {
        return {
          formData: Object.assign({}, prevState.formData, formData),
          dirty: true
        };
      });
      this.debouncedUpdateFormData(formData, this.props.doc._id);
    };
    this.state = {
      dirty: false,
      formData: this.populateFormDataWithExample(),
      initialFormData: this.populateFormDataWithExample(),
      hasError: false
    };
    this.debouncedUpdateFormData = debounce(props.updateFormData, 200);
    this.usageTracker = mutateTrackerInstance(props.usageTracker);
  }
  componentDidMount() {
    const hash = this.props.location.hash.substring(1);
    if (hash && hash.length) {
      window.requestAnimationFrame(() => {
        const el = document.getElementById(`anchor-${decodeURIComponent(hash)}`);
        if (el) {
          el.scrollIntoView();
        }
      });
    }
  }
  populateFormDataWithExample() {
    const {
      operation,
      oas,
      formData
    } = this.props;
    if (operation.requestBody && !removeUndefinedObjects(formData.body)) {
      const exampleRequestBody = getRequestBodyExample(operation, oas, false);
      if (exampleRequestBody) {
        try {
          formData.body = JSON.parse(exampleRequestBody.code);
        } catch (__) {
          // some examples aren't JSON parseable (i.e. "string") so just display the code
          formData.body = exampleRequestBody.code;
        }
      }
    }
    return formData;
  }
  renderCodeSample() {
    const {
      hasError
    } = this.state;
    const {
      doc,
      har,
      isMultipart,
      language,
      oas,
      oasDocs,
      operation,
      setLanguage,
      availableClientLibraries
    } = this.props;
    let examples;
    try {
      examples = doc.api.examples.codes;
    } catch (e) {
      examples = [];
    }
    return /*#__PURE__*/_jsx(ErrorBoundary, {
      hasError: hasError,
      resetFormState: this.resetFormState,
      registerError: this.registerError,
      children: /*#__PURE__*/_jsx(CodeSample, {
        doc: doc,
        har: har,
        isMultipart: isMultipart,
        oas: oas,
        oasDocs: oasDocs,
        setLanguage: setLanguage,
        operation: operation,
        formData: this.state.formData,
        language: language,
        examples: examples,
        availableClientLibraries: availableClientLibraries,
        usageTracker: this.usageTracker
      })
    });
  }
  renderResponse() {
    const {
      doc,
      oas,
      operation
    } = this.props;
    let exampleResponses;
    try {
      exampleResponses = this.props.doc.api.results.codes;
    } catch (e) {
      exampleResponses = [];
    }
    return /*#__PURE__*/_jsx(Response, {
      docId: doc._id,
      oas: oas,
      operation: operation,
      exampleResponses: exampleResponses
    });
  }
  renderParams() {
    const {
      oas,
      operation
    } = this.props;
    return /*#__PURE__*/_jsx(Params, {
      formData: this.state.formData,
      oas: oas,
      onChange: this.onFormDataChange,
      operation: operation,
      usageTracker: this.usageTracker
    });
  }
  renderPathUrl() {
    const {
      doc,
      oas,
      oasDocs,
      operation,
      isMultipart
    } = this.props;
    return /*#__PURE__*/_jsx(PathUrl, {
      doc: doc,
      dirty: this.state.dirty,
      oas: oas,
      oasDocs: oasDocs,
      operation: operation,
      isMultipart: isMultipart,
      usageTracker: this.usageTracker
    });
  }
  render() {
    const {
      hasError
    } = this.state;
    const {
      doc,
      operation
    } = this.props;
    return /*#__PURE__*/_jsxs(StyledGrid, {
      "data-test-id": `doc-${doc.api.method}-${doc.swagger.path}`,
      children: [/*#__PURE__*/_jsxs(StyledPathContainer, {
        size: {
          xs: 12
        },
        children: [/*#__PURE__*/_jsx("a", {
          className: "anchor-page-title",
          id: getDocAnchorId(doc.api.method, doc.swagger.path)
        }), doc.type === 'endpoint' && this.renderPathUrl()]
      }), /*#__PURE__*/_jsxs(StyledLeftColumn, {
        size: {
          xs: 6
        },
        className: "left-column",
        children: [(doc.title || doc.excerpt) && /*#__PURE__*/_jsxs("div", {
          className: "m-bottom-6",
          children: [doc.title && /*#__PURE__*/_jsx(H4, {
            children: /*#__PURE__*/_jsx(Markdown, {
              inline: true,
              children: doc.title
            })
          }), doc.excerpt && /*#__PURE__*/_jsx(Excerpt, {
            children: /*#__PURE__*/_jsx(Markdown, {
              children: doc.excerpt
            })
          })]
        }), /*#__PURE__*/_jsx(Requirements, {
          doc: doc,
          operation: operation
        }), doc.type === 'endpoint' && !hasError && this.renderParams(), doc.body && /*#__PURE__*/_jsx("span", {
          children: "DocBody"
        })]
      }), /*#__PURE__*/_jsxs(StyledRightColumn, {
        size: {
          xs: 6
        },
        className: "right-column",
        children: [doc.type === 'endpoint' && /*#__PURE__*/_jsxs(Fragment, {
          children: [this.renderCodeSample(), this.renderResponse()]
        }), doc.body && /*#__PURE__*/_jsx("span", {
          children: "DocBody"
        })]
      })]
    });
  }
}
Doc.propTypes = {
  usageTracker: PropTypes.object.isRequired,
  doc: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    excerpt: PropTypes.string,
    slug: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    body: PropTypes.string,
    api: PropTypes.shape({
      method: PropTypes.string.isRequired,
      params: PropTypes.object,
      examples: PropTypes.shape({
        codes: PropTypes.arrayOf(PropTypes.shape({
          language: PropTypes.string.isRequired,
          code: PropTypes.string.isRequired
        }))
      }),
      results: PropTypes.shape({
        codes: PropTypes.arrayOf(PropTypes.shape({}))
      })
    }),
    swagger: PropTypes.shape({
      path: PropTypes.string.isRequired
    })
  }).isRequired,
  flags: PropTypes.shape({
    correctnewlines: PropTypes.bool
  }).isRequired,
  formData: PropTypes.object.isRequired,
  har: PropTypes.shape({}).isRequired,
  isMultipart: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  location: PropTypes.object.isRequired,
  oas: OasPropType.isRequired,
  oasDocs: PropTypes.array.isRequired,
  operation: OperationPropType.isRequired,
  setLanguage: PropTypes.func.isRequired,
  updateFormData: PropTypes.func.isRequired,
  availableClientLibraries: PropTypes.array.isRequired
};
Doc.defaultProps = {
  oas: {},
  flags: {
    correctnewlines: false
  }
};
const mapStateToProps = (state, ownProps) => {
  return {
    auth: getAuthSelector(state),
    formData: getFormData(state, ownProps),
    har: getHar(state, ownProps),
    isMultipart: getIsMultipart(state, ownProps),
    oas: getOas(state),
    operation: getOperation(state, ownProps),
    availableClientLibraries: getAvailableClientLibraries(state)
  };
};
const mapDispatchToProps = {
  updateFormData
};
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Doc));